import { DatePicker as MuiDatePicker, DatePickerProps } from '@mui/x-date-pickers-pro';
import FormLabel from '@forms/Components/FormLabel';
import { useMemo, useRef } from 'react';
import { AppInputCustomProps, CustomFieldProps } from '@forms/Components/types';
import { Dayjs } from 'dayjs';
import classNames from 'classnames/bind';
import { dayjsNow, parseDayJs, parseDayJsFromDateInput } from '@/Infrastructure/Utilities/dayjs.type';
import DisabledTooltip from '@components/DisabledTooltip';
import { CustomDateRangePickerAdornment } from './DateRangePicker';
import ClearInputAdornment from './ClearInputAdornment';

import styles from './Form.module.css';
const styleContext = classNames.bind(styles);

/**
 * Wrapper around MUI DatePicker to provide application-wide default behaviors.
 */
export default function DatePicker({ customProps, ...props }: DatePickerProps & AppInputCustomProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const dynamicClasses = styleContext({
    input: true,
    dirty: customProps.isDirty,
    error: customProps.isError,
    disabled: props.disabled,
    empty: !props.value,
    [props.className ?? '']: !!props.className
  });

  const blurEventHandler = () => {
    const rawValue = inputRef.current?.value || '';
    // If it's already a valid dayjs obj return without doing anything
    if (parseDayJs(rawValue)?.isValid()) {
      return;
    }

    const dayJsValue = parseDayJsFromDateInput(rawValue);

    if (dayJsValue?.isValid() && props.onChange) {
      props.onChange(dayJsValue, { validationError: null, source: 'field' });
    }
  };

  return (
    <DisabledTooltip
      message={customProps.disabledReason}
      className={customProps.disabledClass}>
      <MuiDatePicker
        {...props}
        className={dynamicClasses}
        label={
          <FormLabel
            text={customProps.labelText}
            error={customProps.isError}
            errorText={customProps.errorText}
            required={customProps.required}
          />
        }
        disabled={props.disabled}
        autoFocus={props.autoFocus}
        slotProps={{
          textField: {
            inputRef: inputRef,
            variant: customProps.variant,
            onBlur: blurEventHandler,
            error: customProps.isError,
            slotProps: {
              input: {
                className: styles.input,
                slotProps: {
                  root: {
                    className: styleContext({
                      root: true,
                      'date-picker': true
                    })
                  }
                },
                startAdornment: <CustomDateRangePickerAdornment />,
                endAdornment: (
                  <ClearInputAdornment
                    onClear={customProps.onClear}
                    inputValue={props.value}
                    inputIsDisabled={props.disabled}
                  />
                )
              }
            }
          }
        }}
      />
    </DisabledTooltip>
  );
}

/**
 * HOC Component integrating our DatePicker component as a Formik Field
 */
function asDatePicker() {
  return ({ field, form, ...props }: CustomFieldProps) => {
    const { value, initialValue, error } = form.getFieldMeta<Dayjs | null>(field.name);
    const { dirty } = form;

    const isDirty = useMemo(() => {
      if (dirty) {
        if (value && initialValue) {
          // Determine if the dayjs value is different
          return value.diff(initialValue) !== 0;
        } else if (!value && !initialValue) {
          // Input was null, and is still null
          return false;
        } else {
          // It was null, and now it is not or vice versa
          return true;
        }
      }
      return false;
    }, [value, initialValue, dirty]);

    return (
      <DatePicker
        name={field.name}
        className={props.className}
        value={field.value as Dayjs}
        onChange={(value) => {
          void form.setFieldValue(field.name, value, true);
        }}
        disabled={props.disabled || form.isSubmitting}
        customProps={{
          labelText: props.label ?? '',
          onBlur: (event) => {
            const dayJsField = field.value as Dayjs | null;

            /** When tabbing out of the input, if only the year is missing they want it to be automagically entered */
            if ((!dayJsField || !dayJsField.isValid()) && event.target.value) {
              const inputs = event.target.value.split('/');
              const month = Number(inputs[0]);
              const day = Number(inputs[1]);
              const year = Number(inputs[2]);
              if (month && day && !year) {
                void form.setFieldValue(field.name, parseDayJs(`${String(month)}/${String(day)}/${String(dayjsNow().year())}`), true);
              }
            }
          },
          onClear: () => {
            void form.setFieldTouched(field.name, true);
            void form.setFieldValue(field.name, null);
          },
          isDirty: isDirty,
          isError: Boolean(form.dirty && error),
          errorText: error,
          required: props.required,
          disabledReason: props.disabledReason,
          disabledClass: props.disabledTooltipClass
        }}
      />
    );
  };
}

export const FormikDatePicker = asDatePicker();
